import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { LucideIconsModule } from 'src/app/shared/icons/lucide-icons.module';
import {
  MsalAuthError,
  MsalClientService,
} from 'src/app/services/infra/oauthAuthorizationCodeClientService/msalClientService/msal-client-service.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { environment } from 'src/environments/environment';

export type AttackStrategy = 'general' | 'hr_data' | 'financial_data' | 'custom';

export interface CreateAssessmentResult {
  readonly testName: string;
  readonly attackStrategy: AttackStrategy;
  readonly customContext: string | null;
  readonly authCode: string;
}

@Component({
  selector: 'app-create-assessment-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, LabelsProviderPipe, LucideIconsModule],
  templateUrl: './create-assessment-dialog.component.html',
  styleUrl: './create-assessment-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateAssessmentDialogComponent {
  private readonly _dialogRef = inject(MatDialogRef<CreateAssessmentDialogComponent>);
  private readonly _msalClient = inject(MsalClientService);
  private readonly _logger = inject(LoggerService);

  readonly testName = signal('');
  readonly selectedStrategy = signal<AttackStrategy | null>(null);
  readonly customContext = signal('');
  readonly authCode = signal<string | null>(null);
  readonly authenticatedUser = signal<string | null>(null);
  readonly isAuthenticating = signal(false);
  readonly authError = signal<string | null>(null);
  readonly isProduction = environment.production;
  readonly maxCustomLength = 200;

  get isFormValid(): boolean {
    if (!this.testName().trim()) {
      return false;
    }
    if (!this.selectedStrategy()) {
      return false;
    }
    if (this.selectedStrategy() === 'custom' && !this.customContext().trim()) {
      return false;
    }
    if (!this.authCode()) {
      return false;
    }
    return true;
  }

  onCancel(): void {
    this._dialogRef.close();
  }

  onRunAssessment(): void {
    if (!this.isFormValid) {
      return;
    }
    const strategy = this.selectedStrategy();
    const code = this.authCode();
    if (!strategy || !code) {
      return;
    }
    const result: CreateAssessmentResult = {
      testName: this.testName().trim(),
      attackStrategy: strategy,
      customContext: strategy === 'custom' ? this.customContext().trim() : null,
      authCode: code,
    };
    this._dialogRef.close(result);
  }

  onSignIn(): void {
    this.isAuthenticating.set(true);
    this.authError.set(null);
    this._msalClient.popUpMicrosoftLoginToRetrieveAuthCode().subscribe({
      next: ({ code, email }) => {
        this.authCode.set(code);
        this.authenticatedUser.set(email || null);
        this.isAuthenticating.set(false);
      },
      error: (err: MsalAuthError | unknown) => {
        this._logger.error('Microsoft sign-in failed', err as object);
        this.isAuthenticating.set(false);
        this._handleAuthError(err);
      },
    });
  }

  onChangeUser(): void {
    this.authCode.set(null);
    this.authenticatedUser.set(null);
    this.authError.set(null);
    this.onSignIn();
  }

  onUseTestAccount(): void {
    this.authCode.set('dev-test-auth-code');
    this.authenticatedUser.set('test-user@dev.local');
    this.authError.set(null);
  }

  private _handleAuthError(err: MsalAuthError | unknown): void {
    if (typeof err === 'object' && err !== null && 'needsAdminConsent' in err) {
      const msalErr = err as MsalAuthError;
      if (msalErr.needsAdminConsent) {
        this.authError.set('adminConsentRequired');
        return;
      }
    }
    this.authError.set('signInFailed');
  }

  onCustomContextChange(value: string): void {
    this.customContext.set(value.slice(0, this.maxCustomLength));
  }

  onSelectStrategy(strategy: AttackStrategy): void {
    this.selectedStrategy.set(strategy);
  }
}
